'use client';

import { ProductCard } from './ProductCard';
import { Skeleton } from '@/components/ui/skeleton';
import type { Product } from '@/types';
import { useMemo } from 'react';
import { useLocale } from '@/hooks/useLocale';
import { t } from '@/i18n/t';

interface ProductGridProps {
  products: Product[];
  loading?: boolean;
}

export function ProductGrid({ products, loading }: ProductGridProps) {
  const { locale } = useLocale();

  /**
   * Vilka attribut SKILJER de visade produkterna åt?
   *
   * Kortet hade tidigare en fast lista över vad som fick visas, och `Spänning
   * [V]` stod på den. En sökning efter generator till en VW Touran gav därför
   * fem träffar som alla hette "Generator", alla från JP Group, alla märkta
   * "14V" — det enda attribut som var identiskt för samtliga. Det avgörande,
   * laddströmmen (110, 140 och 150 A), fanns i datan men visades aldrig.
   * Varken en verkstad eller en privatperson kunde välja.
   *
   * Nu räknas det ut per resultatsida: attribut vars värde varierar mellan
   * korten lyfts fram, det som är lika för alla utelämnas. Regeln behöver ingen
   * underhållen lista per kategori — bromsskivor får diameter, generatorer
   * ampere, filter höjd, av sig själva.
   */
  const highlightPerProduct = useMemo(() => {
    // Jämförelsen görs PER KATEGORI, inte per sida. En resultatsida blandar
    // generatorer, generatorregulatorer och remskivor; räknar man över alltihop
    // vinner attribut som bara finns på några få kort, och just de korten får
    // ingenting att visa. Det är inom kategorin kunden faktiskt väljer.
    const perKategori = new Map<string, Map<string, Set<string>>>();
    const antalPerKategori = new Map<string, number>();
    for (const p of products) {
      const kat = String(p.id_category_default ?? p.category_name ?? '—');
      antalPerKategori.set(kat, (antalPerKategori.get(kat) ?? 0) + 1);
      if (!perKategori.has(kat)) perKategori.set(kat, new Map());
      const attr = perKategori.get(kat)!;
      for (const [k, v] of Object.entries(p.key_features || {})) {
        if (!v) continue;
        if (!attr.has(k)) attr.set(k, new Set());
        attr.get(k)!.add(String(v));
      }
    }

    const valdaPerKategori = new Map<string, string[]>();
    for (const [kat, attr] of perKategori) {
      const antal = antalPerKategori.get(kat) ?? 0;
      if (antal < 2) { valdaPerKategori.set(kat, []); continue; }
      const valda = [...attr.entries()]
        // Måste variera, men inte vara näst intill unikt per produkt — då är det
        // ett serienummer snarare än en egenskap man väljer på.
        .filter(([, vals]) => vals.size > 1 && vals.size <= Math.max(2, Math.ceil(antal * 0.8)))
        .sort((a, b) => b[1].size - a[1].size)
        .slice(0, 2)
        .map(([k]) => k);
      valdaPerKategori.set(kat, valda);
    }
    return valdaPerKategori;
  }, [products]);

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={i} className="space-y-4">
            <Skeleton className="aspect-square w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-8 w-full" />
          </div>
        ))}
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">{t(locale, 'product.noProductsFound')}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
      {products.map((product, i) => (
        <ProductCard
          key={product.id}
          product={product}
          priority={i < 8}
          highlightFeatures={highlightPerProduct.get(String(product.id_category_default ?? product.category_name ?? '—')) ?? []}
        />
      ))}
    </div>
  );
}
